'use client'

import Image from 'next/image'

import { cn } from '@/_lib/utils'

/** `id` of the tab panel below the tablist (see {@link AStepSelectAmbulanceCategoryTabs}). */
export const STEP_SELECT_AMBULANCE_CATEGORY_PANEL_ID = 'step-select-ambulance-category-panel'

/**
 * One tab in the horizontal category picker (id + label + optional thumbnail).
 *
 * @property id - Stable key from the API category; matched against {@link AStepSelectAmbulanceCategoryTabsProps.activeId}.
 * @property label - Short name shown next to the image (e.g. singular category title).
 * @property imageUrl - Optional circular preview; omit or `null` when the category has no image.
 */
export type AStepSelectAmbulanceCategoryTabsItem = {
	id: string | number
	label: string
	imageUrl: string | null | undefined
}

/**
 * Props for {@link AStepSelectAmbulanceCategoryTabs}.
 *
 * @property items - Categories to render as `role="tab"` buttons; empty list renders nothing.
 * @property activeId - Currently selected category id, or `null` before the first selection.
 * @property onSelect - Called with the clicked item’s `id` when the user changes tab.
 * @property className - Optional root wrapper classes (layout / width).
 * @property categoryPanelId - DOM id of the controlled region; defaults to {@link STEP_SELECT_AMBULANCE_CATEGORY_PANEL_ID}.
 */
export type AStepSelectAmbulanceCategoryTabsProps = {
	items: AStepSelectAmbulanceCategoryTabsItem[]
	activeId: string | number | null
	onSelect: (id: string | number) => void
	className?: string
	/** @default STEP_SELECT_AMBULANCE_CATEGORY_PANEL_ID */
	categoryPanelId?: string
}

/**
 * Horizontal tablist of ambulance top-level categories: thumbnail + label per option.
 *
 * @param props - {@link AStepSelectAmbulanceCategoryTabsProps}
 * @returns `null` when `items` is empty; otherwise a `role="tablist"` row of category tabs.
 */
export function AStepSelectAmbulanceCategoryTabs({
	items,
	activeId,
	onSelect,
	className,
	categoryPanelId = STEP_SELECT_AMBULANCE_CATEGORY_PANEL_ID
}: AStepSelectAmbulanceCategoryTabsProps) {
	if (!items.length) return null

	return (
		<div
			className={cn('flex flex-wrap gap-2 md:gap-3', className)}
			role='tablist'
			aria-label='Kategórie'
		>
			{items.map(item => {
				const isActive = item.id === activeId

				return (
					<button
						key={item.id}
						type='button'
						role='tab'
						aria-selected={isActive}
						aria-controls={categoryPanelId}
						onClick={() => onSelect(item.id)}
						className={cn(
							'flex min-w-0 items-center gap-2 rounded-full border border-solid pl-1.5 pr-3 py-1.5',
							'text-left text-sm font-normal',
							'cursor-pointer transition duration-300 group',
							'border-medante-purple-dark text-medante-purple-dark',
							'hover:border-medante-purple-dark/80  hover:text-medante-purple-dark/80',
							!isActive && 'opacity-50 hover:opacity-85'
						)}
					>
						<span
							className={cn(
								'relative size-8 shrink-0 overflow-hidden rounded-full bg-medante-purple-dark/10'
							)}
							aria-hidden
						>
							{item.imageUrl ? (
								<Image
									src={item.imageUrl}
									alt=''
									fill
									sizes='32px'
									className='object-cover object-top origin-top scale-[1.5]'
									unoptimized={true}
								/>
							) : null}
						</span>
						<span className='min-w-0 pr-1 leading-snug'>{item.label}</span>
					</button>
				)
			})}
		</div>
	)
}
